import {
  memo,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type RefObject,
} from "react";

import citrusAvatarJson from "~/assets/avatars/citrus.avatar.json";
import cloudeeAvatarJson from "~/assets/avatars/cloudee.avatar.json";
import cubeeAvatarJson from "~/assets/avatars/cubee.avatar.json";
import grokBotAvatarJson from "~/assets/avatars/grok-bot.avatar.json";
import kirbyAvatarJson from "~/assets/avatars/kirby.avatar.json";
import oneeAvatarJson from "~/assets/avatars/onee.avatar.json";
import {
  body as suneeBody,
  colors as suneeColors,
  name as suneeName,
} from "~/assets/avatars/sunee.avatar.json";
import {
  body as unknownCylinderBody,
  colors as unknownCylinderColors,
  name as unknownCylinderName,
} from "~/assets/avatars/unknown-cylinder.avatar.json";
import {
  body as unknownDiamondBody,
  colors as unknownDiamondColors,
  name as unknownDiamondName,
} from "~/assets/avatars/unknown-diamond.avatar.json";
import { cn } from "~/lib/utils";

export type AvatarLabAnimation = keyof typeof cloudeeAvatarJson.animations;
export type AvatarLabExpression = keyof typeof cloudeeAvatarJson.expressions;

type EyePose = { width: number; height: number; x: number; y: number; angle: number };
type Expression = {
  head: { x: number; y: number; z: number };
  eyes: { left: EyePose; right: EyePose; spacing: number };
  colors?: { body?: string; eyes?: string };
};
type AnimationStep = {
  expression: AvatarLabExpression;
  holdMs: number;
  transitionMs: number;
};
type Animation = {
  steps: AnimationStep[];
  blink: {
    enabled: boolean;
    initialDelayMs: number;
    minIntervalMs: number;
    maxIntervalMs: number;
    durationMs: number;
  };
};
type AvatarSurface = {
  type: "capsule" | "cone" | "cube" | "cylinder" | "diamond" | "sphere";
  width: number;
  height: number;
  depth: number;
  roundness: number;
};
type AvatarNode = {
  surface: AvatarSurface;
  position: number[];
  rotation: number[];
};
type AvatarDefinition = {
  name: string;
  body: { primary: AvatarSurface; nodes: AvatarNode[] };
  colors: { body: string; eyes: string };
  expressions: Record<AvatarLabExpression, Expression>;
  animations: Record<AvatarLabAnimation, Animation>;
};

const grokBotDefinition = grokBotAvatarJson as unknown as AvatarDefinition;

function withSharedBehavior(
  geometry: { name: string; body: unknown; colors: unknown },
  behavior: AvatarDefinition,
): AvatarDefinition {
  return {
    ...behavior,
    name: geometry.name,
    body: geometry.body as AvatarDefinition["body"],
    colors: geometry.colors as AvatarDefinition["colors"],
  };
}

// These exports have byte-identical expression and animation tables. Sharing
// one table keeps the initial web bundle smaller without changing their faces.
const suneeDefinition = withSharedBehavior(
  { name: suneeName, body: suneeBody, colors: suneeColors },
  grokBotDefinition,
);
const unknownDiamondDefinition = withSharedBehavior(
  { name: unknownDiamondName, body: unknownDiamondBody, colors: unknownDiamondColors },
  grokBotDefinition,
);
const unknownCylinderDefinition = withSharedBehavior(
  { name: unknownCylinderName, body: unknownCylinderBody, colors: unknownCylinderColors },
  grokBotDefinition,
);

const avatarCatalog = [
  { id: "cloudee", definition: cloudeeAvatarJson as unknown as AvatarDefinition },
  { id: "grok-bot", definition: grokBotDefinition },
  { id: "sunee", definition: suneeDefinition },
  { id: "diamond", definition: unknownDiamondDefinition },
  { id: "citrus", definition: citrusAvatarJson as unknown as AvatarDefinition },
  { id: "onee", definition: oneeAvatarJson as unknown as AvatarDefinition },
  { id: "cubee", definition: cubeeAvatarJson as unknown as AvatarDefinition },
  { id: "kirby", definition: kirbyAvatarJson as unknown as AvatarDefinition },
  { id: "cylinder", definition: unknownCylinderDefinition },
] as const;

export type ThreadAvatarId = (typeof avatarCatalog)[number]["id"];
export const THREAD_AVATAR_IDS = avatarCatalog.map(({ id }) => id);
const avatarCatalogById = new Map(avatarCatalog.map((entry) => [entry.id, entry]));

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const reducedMotionListeners = new Set<() => void>();
let reducedMotionMedia: MediaQueryList | null = null;

function getReducedMotionMedia(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  reducedMotionMedia ??= window.matchMedia(REDUCED_MOTION_QUERY);
  return reducedMotionMedia;
}

function emitReducedMotionChange(): void {
  for (const listener of reducedMotionListeners) listener();
}

function subscribeReducedMotion(listener: () => void): () => void {
  const media = getReducedMotionMedia();
  if (!media) return () => {};
  if (reducedMotionListeners.size === 0) {
    media.addEventListener("change", emitReducedMotionChange);
  }
  reducedMotionListeners.add(listener);
  return () => {
    reducedMotionListeners.delete(listener);
    if (reducedMotionListeners.size === 0) {
      media.removeEventListener("change", emitReducedMotionChange);
    }
  };
}

function getReducedMotionSnapshot(): boolean {
  return getReducedMotionMedia()?.matches ?? false;
}

function getReducedMotionServerSnapshot(): boolean {
  return false;
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export function resolveThreadAvatarId(seed: string): ThreadAvatarId {
  return avatarCatalog[stableHash(seed) % avatarCatalog.length]!.id;
}

function resolveAvatarEntry(seed: string, avatarId?: ThreadAvatarId) {
  const resolvedId = avatarId ?? resolveThreadAvatarId(seed);
  return avatarCatalogById.get(resolvedId) ?? avatarCatalog[0];
}

function mixHexColor(from: string, to: string, amount: number): string {
  const channel = (color: string, offset: number) =>
    Number.parseInt(color.replace("#", "").slice(offset, offset + 2), 16);
  const mixed = [0, 2, 4].map((offset) =>
    Math.round(channel(from, offset) + (channel(to, offset) - channel(from, offset)) * amount),
  );
  return `#${mixed.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function roundGeometry(value: number): number {
  return Math.round(value * 100) / 100;
}

function eyeGeometry(eye: EyePose, side: "left" | "right", spacing: number) {
  const direction = side === "left" ? -1 : 1;
  const centerX = direction * (spacing / 2 + eye.width / 2) + eye.x;
  return {
    angle: roundGeometry(eye.angle),
    cx: roundGeometry(centerX),
    cy: roundGeometry(eye.y),
    rx: roundGeometry(eye.width / 2),
    ry: roundGeometry(eye.height / 2),
  };
}

const avatarViewBoxCache = new WeakMap<AvatarDefinition, string>();

function avatarViewBox(definition: AvatarDefinition): string {
  const cached = avatarViewBoxCache.get(definition);
  if (cached) return cached;
  const primary = definition.body.primary;
  let extent = Math.max(primary.width, primary.height) / 2;
  for (const node of definition.body.nodes) {
    const x = Math.abs(node.position[0] ?? 0) + node.surface.width / 2;
    const y = Math.abs(node.position[1] ?? 0) + node.surface.height / 2;
    extent = Math.max(extent, x, y);
  }
  const paddedExtent = roundGeometry(Math.max(125, extent * 1.05));
  const viewBox = `${-paddedExtent} ${-paddedExtent} ${paddedExtent * 2} ${paddedExtent * 2}`;
  avatarViewBoxCache.set(definition, viewBox);
  return viewBox;
}

function useVisible(elementRef: RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry) setVisible(entry.isIntersecting);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, enabled]);
  return visible;
}

function useBlink(
  eyesRef: RefObject<SVGGElement | null>,
  enabled: boolean,
  blink: Animation["blink"],
  seed: string,
): void {
  useEffect(() => {
    const eyes = eyesRef.current;
    if (!enabled || !blink.enabled || !eyes) return;
    let blinkTimer: number | undefined;
    let reopenTimer: number | undefined;
    let cancelled = false;
    const intervalRange = Math.max(0, blink.maxIntervalMs - blink.minIntervalMs);
    const interval = blink.minIntervalMs + (stableHash(`${seed}:blink`) % (intervalRange + 1));
    const scheduleBlink = (delay: number) => {
      blinkTimer = window.setTimeout(() => {
        eyes.classList.add("avatar-lab-avatar__eyes--closed");
        reopenTimer = window.setTimeout(() => {
          eyes.classList.remove("avatar-lab-avatar__eyes--closed");
          if (!cancelled) scheduleBlink(interval);
        }, blink.durationMs);
      }, delay);
    };
    scheduleBlink(blink.initialDelayMs + (stableHash(seed) % 700));
    return () => {
      cancelled = true;
      if (blinkTimer !== undefined) window.clearTimeout(blinkTimer);
      if (reopenTimer !== undefined) window.clearTimeout(reopenTimer);
      eyes.classList.remove("avatar-lab-avatar__eyes--closed");
    };
  }, [
    blink.durationMs,
    blink.enabled,
    blink.initialDelayMs,
    blink.maxIntervalMs,
    blink.minIntervalMs,
    enabled,
    eyesRef,
    seed,
  ]);
}

function SurfaceShape({
  surface,
  highlightColor,
}: {
  surface: AvatarSurface;
  highlightColor: string;
}) {
  const width = surface.width;
  const height = surface.height;
  const x = -width / 2;
  const y = -height / 2;
  switch (surface.type) {
    case "capsule":
      return (
        <rect
          x={roundGeometry(x)}
          y={roundGeometry(y)}
          width={roundGeometry(width)}
          height={roundGeometry(height)}
          rx={roundGeometry(Math.min(width, height) / 2)}
        />
      );
    case "cone": {
      const halfWidth = width / 2;
      const halfHeight = height / 2;
      const baseCurve = Math.min(22, height * 0.12);
      return (
        <path
          d={`M 0 ${roundGeometry(-halfHeight)} Q ${roundGeometry(-halfWidth * 0.9)} ${roundGeometry(
            -height * 0.04,
          )} ${roundGeometry(-halfWidth)} ${roundGeometry(
            halfHeight - baseCurve,
          )} Q 0 ${roundGeometry(halfHeight + baseCurve)} ${roundGeometry(
            halfWidth,
          )} ${roundGeometry(halfHeight - baseCurve)} Q ${roundGeometry(
            halfWidth * 0.9,
          )} ${roundGeometry(-height * 0.04)} 0 ${roundGeometry(-halfHeight)} Z`}
        />
      );
    }
    case "cube": {
      const radius = Math.min(width, height) * Math.min(0.22, surface.roundness * 0.16);
      return (
        <rect
          x={roundGeometry(x)}
          y={roundGeometry(y)}
          width={roundGeometry(width)}
          height={roundGeometry(height)}
          rx={roundGeometry(radius)}
        />
      );
    }
    case "cylinder": {
      const capHeight = Math.min(height * 0.22, 24);
      return (
        <>
          <rect
            x={roundGeometry(x)}
            y={roundGeometry(y)}
            width={roundGeometry(width)}
            height={roundGeometry(height)}
            rx={roundGeometry(capHeight / 2)}
          />
          <ellipse
            cx="0"
            cy={roundGeometry(y + capHeight / 2)}
            rx={roundGeometry(width / 2)}
            ry={roundGeometry(capHeight / 2)}
            fill={highlightColor}
            opacity="0.2"
          />
        </>
      );
    }
    case "diamond":
      return (
        <polygon
          points={`0,${roundGeometry(y)} ${roundGeometry(width / 2)},0 0,${roundGeometry(
            height / 2,
          )} ${roundGeometry(x)},0`}
        />
      );
    case "sphere":
      return <ellipse cx="0" cy="0" rx={roundGeometry(width / 2)} ry={roundGeometry(height / 2)} />;
  }
}

// Clean-room SVG renderer for the user's Avatar Lab JSON. No lab runtime is bundled.
export const AvatarLabAvatar = memo(function AvatarLabAvatar({
  seed,
  avatarId,
  animation = "idle",
  expression: expressionOverride,
  animated = false,
  size,
  className,
  ariaHidden = false,
}: {
  seed: string;
  avatarId?: ThreadAvatarId;
  animation?: AvatarLabAnimation;
  expression?: AvatarLabExpression;
  animated?: boolean;
  size: number;
  className?: string;
  ariaHidden?: boolean;
}) {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const eyesRef = useRef<SVGGElement | null>(null);
  const avatarEntry = resolveAvatarEntry(seed, avatarId);
  const definition = avatarEntry.definition;
  const gradientId = `avatar-lab-${useId().replaceAll(":", "")}`;
  const prefersReducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );
  const isVisible = useVisible(containerRef, animated);
  const animationDefinition = definition.animations[animation];
  const playbackKey = `${seed}:${animation}`;
  const initialStepIndex = stableHash(playbackKey) % animationDefinition.steps.length;
  const [playbackState, setPlaybackState] = useState(() => ({
    key: playbackKey,
    stepIndex: initialStepIndex,
  }));
  const stepIndex = playbackState.key === playbackKey ? playbackState.stepIndex : initialStepIndex;
  const playbackEnabled = animated && isVisible && !prefersReducedMotion;

  const step = animationDefinition.steps[stepIndex] ?? animationDefinition.steps[0];
  useEffect(() => {
    if (
      expressionOverride !== undefined ||
      !playbackEnabled ||
      animationDefinition.steps.length < 2 ||
      !step
    ) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setPlaybackState({
        key: playbackKey,
        stepIndex: (stepIndex + 1) % animationDefinition.steps.length,
      });
    }, step.holdMs + step.transitionMs);
    return () => window.clearTimeout(timeout);
  }, [
    animationDefinition.steps.length,
    expressionOverride,
    playbackEnabled,
    playbackKey,
    step,
    stepIndex,
  ]);
  useBlink(eyesRef, playbackEnabled, animationDefinition.blink, seed);

  if (!step) return null;
  const expressionName = expressionOverride ?? step.expression;
  const expression = definition.expressions[expressionName] ?? definition.expressions.neutral;
  if (!expression) return null;

  const transitionMs = playbackEnabled
    ? expressionOverride === undefined
      ? step.transitionMs
      : 180
    : 0;
  const bodyColor = expression.colors?.body ?? definition.colors.body;
  const eyeColor = expression.colors?.eyes ?? definition.colors.eyes;
  const bodyHighlight = mixHexColor(bodyColor, "#ffffff", 0.32);
  const bodyShadow = mixHexColor(bodyColor, "#000000", 0.16);
  const poseX = roundGeometry(expression.head.y * 0.15);
  const poseY = roundGeometry(expression.head.x * 0.1);
  const poseRotation = roundGeometry(expression.head.z * 0.35);
  const poseStyle: CSSProperties = {
    transform: `translate(${poseX}px, ${poseY}px) rotate(${poseRotation}deg)`,
    transformBox: "view-box",
    transformOrigin: "center",
    transitionDuration: `${transitionMs}ms`,
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
  };
  const eyePresentation = (eye: EyePose, side: "left" | "right") => {
    const geometry = eyeGeometry(eye, side, expression.eyes.spacing);
    return {
      geometry,
      style: {
        cx: `${geometry.cx}px`,
        cy: `${geometry.cy}px`,
        rx: `${geometry.rx}px`,
        ry: `${geometry.ry}px`,
        transitionDuration: `${transitionMs}ms`,
        transitionProperty: "cx, cy, rx, ry",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
      } as CSSProperties,
    };
  };
  const leftEye = eyePresentation(expression.eyes.left, "left");
  const rightEye = eyePresentation(expression.eyes.right, "right");

  return (
    <span
      ref={containerRef}
      className={cn("avatar-lab-avatar relative block shrink-0", className)}
      style={{ width: size, height: size }}
      role={ariaHidden ? undefined : "img"}
      aria-hidden={ariaHidden || undefined}
      aria-label={ariaHidden ? undefined : `${definition.name} — ${expressionName}`}
      data-animation={animation}
      data-animated={playbackEnabled}
      data-avatar-id={avatarEntry.id}
      data-avatar-name={definition.name}
      data-expression={expressionName}
      data-testid="avatar-lab-avatar"
    >
      <svg
        width={size}
        height={size}
        viewBox={avatarViewBox(definition)}
        overflow="visible"
        focusable="false"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={gradientId} cx="32%" cy="24%" r="78%">
            <stop offset="0%" stopColor={bodyHighlight} />
            <stop offset="58%" stopColor={bodyColor} />
            <stop offset="100%" stopColor={bodyShadow} />
          </radialGradient>
        </defs>
        <g style={poseStyle}>
          <g className="avatar-lab-avatar__body" fill={`url(#${gradientId})`}>
            {definition.body.nodes.map((node) => (
              <g
                key={`${node.surface.type}:${node.position.join(":")}:${node.surface.width}:${node.surface.height}`}
                transform={`translate(${roundGeometry(node.position[0] ?? 0)} ${roundGeometry(
                  node.position[1] ?? 0,
                )}) rotate(${roundGeometry(node.rotation[2] ?? 0)})`}
              >
                <SurfaceShape surface={node.surface} highlightColor={bodyHighlight} />
              </g>
            ))}
            <SurfaceShape surface={definition.body.primary} highlightColor={bodyHighlight} />
          </g>
          <g
            ref={eyesRef}
            className="avatar-lab-avatar__eyes"
            fill={eyeColor}
            style={
              {
                "--avatar-lab-blink-ms": `${animationDefinition.blink.durationMs}ms`,
              } as CSSProperties
            }
          >
            <ellipse
              cx={leftEye.geometry.cx}
              cy={leftEye.geometry.cy}
              rx={leftEye.geometry.rx}
              ry={leftEye.geometry.ry}
              transform={`rotate(${leftEye.geometry.angle} ${leftEye.geometry.cx} ${leftEye.geometry.cy})`}
              style={leftEye.style}
            />
            <ellipse
              cx={rightEye.geometry.cx}
              cy={rightEye.geometry.cy}
              rx={rightEye.geometry.rx}
              ry={rightEye.geometry.ry}
              transform={`rotate(${rightEye.geometry.angle} ${rightEye.geometry.cx} ${rightEye.geometry.cy})`}
              style={rightEye.style}
            />
          </g>
        </g>
      </svg>
    </span>
  );
});

export const CloudeeAvatar = AvatarLabAvatar;
export type CloudeeAnimation = AvatarLabAnimation;
